import * as React from 'react'

import type { SkillSummary } from '@/hooks/use-profile'
import { cn } from '@/lib/utils'
import type { TopSkillLinkedEvidence } from '@/utils/top-skill-evidence-links'

import { buildTopSkillAriaLabel, TopSkillTooltip } from './top-skill-tooltip'

const pillBorderClass = 'border border-foreground/30'

export function TopSkillChip({
  summary,
  linkedEvidence,
  onOpenSkill,
  displayOnly = false,
  showTooltip = true,
  className,
}: {
  summary: SkillSummary
  linkedEvidence?: TopSkillLinkedEvidence
  onOpenSkill?: () => void
  /** When true, render as a non-interactive pill (tooltip only). */
  displayOnly?: boolean
  /** When false, skip the evidence/level tooltip (public profile chips). */
  showTooltip?: boolean
  className?: string
}) {
  const openRow = () => {
    if (displayOnly) return
    onOpenSkill?.()
  }

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (displayOnly) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openRow()
    }
  }

  const pillClassName = cn(
    'inline-flex max-w-full items-center rounded-full bg-background px-3 py-1 text-left text-sm font-medium',
    pillBorderClass,
    !displayOnly &&
      'cursor-pointer transition-colors duration-150 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    className,
  )

  const pill = (
    <div
      role={displayOnly ? undefined : 'button'}
      tabIndex={displayOnly ? undefined : 0}
      className={pillClassName}
      onClick={displayOnly ? undefined : openRow}
      onKeyDown={displayOnly ? undefined : handleRowKeyDown}
      aria-label={showTooltip ? buildTopSkillAriaLabel(summary, linkedEvidence) : summary.skill_name}
    >
      <span className="min-w-0 truncate">{summary.skill_name}</span>
    </div>
  )

  if (!showTooltip) {
    return pill
  }

  return (
    <TopSkillTooltip summary={summary} linkedEvidence={linkedEvidence}>
      {pill}
    </TopSkillTooltip>
  )
}
